import { Collapse, Typography } from '@material-ui/core';
import { Stack, useSnackbarStack, VisibilityObserver } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'es-toolkit/compat';
import { useEffect, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useAPISocketMessages } from 'shared/api/APISocketMessages';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { trackEvent } from 'shared/helpers/Analytics';
import { useVisibilityState } from 'shared/helpers/BrowserHelpers';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { WebviewLayout } from 'shared/layout/WebviewLayout';
import { useNewLoadIndicatorOnboarding } from 'shared/modules/load-suggestions/core/LoadSuggestionOnboarding';
import { LoadSuggestionsLoadingSkeleton } from 'shared/modules/load-suggestions/core/LoadSuggestionsLoadingSkeleton';
import { LoadSuggestionsNotFound } from 'shared/modules/load-suggestions/core/LoadSuggestionsNotFound';
import { LoadSuggestionSwipeToDismiss } from 'shared/modules/load-suggestions/core/LoadSuggestionSwipeToDismiss';
import {
  setLoadSuggestionsQueryData,
  useDismissLoadSuggestion,
  useLoadSuggestions,
  useLoadSuggestionsCache,
  useLoadSuggestionsUnseenCount,
  useMarkAsSeenLoadSuggestion,
} from 'shared/modules/load-suggestions/data/LoadSuggestionsAPI';
import { LoadSuggestionsListItem } from 'shared/modules/load-suggestions/mobile/LoadSuggestionsListItem';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import styled from 'styled-components';
import { trackDriverOpenedSuggestion } from '../../core/DriverSuggestedLoadAnalytics';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import { PostingDetailsPage } from '../posting-details/PostingDetailsPage';
import { LoadSuggestionBenefitsBanner } from './LoadSuggestionBenefitsBanner';

const viewedUnseenLoadSuggestionGuids = new Set<number>();

const NotFoundPageWrapper = styled(Box)`
  display: flex;
  align-items: center;
  margin-top: 100px;
`;

function useCounterUpdater() {
  const { invalidateLoadSuggestions } = useLoadSuggestionsCache();
  const { data } = useLoadSuggestionsUnseenCount();
  const unseenCount = data?.number_of_unseen_suggestions;
  const visibilityState = useVisibilityState();

  useEffect(() => {
    if (visibilityState === 'hidden') {
      invalidateLoadSuggestions();
      viewedUnseenLoadSuggestionGuids.clear();
    }
  }, [invalidateLoadSuggestions, visibilityState]);

  useEffect(() => {
    if (unseenCount != null) {
      MobileAppBridge.updateSuggestedLoads({ unseen_count: unseenCount });
    }
  }, [unseenCount]);
}

export function LoadSuggestionPage() {
  const { addSnackbar } = useSnackbarStack();
  const { invalidateLoadSuggestions } = useLoadSuggestionsCache();
  const { data: loads, isLoading, isError } = useLoadSuggestions([]);
  const { mutate: markAsSeen } = useMarkAsSeenLoadSuggestion({
    invalidateCache: false,
  });
  const [shouldAnimateOnEnter, setShouldAnimateOnEnter] = useState(false);
  const { shouldRunOnboarding } = useNewLoadIndicatorOnboarding(loads);

  const { mutate: dismiss, isPending: isDismissing } = useDismissLoadSuggestion(
    {
      onError: () => {
        addSnackbar('Failed to dismiss', {
          variant: 'error',
          key: 'dismiss-error-alert-key',
        });
      },
      onSuccess: () => {
        addSnackbar('Load suggestion dismissed', {
          variant: 'success',
          key: 'dismiss-alert-key',
        });
      },
    },
  );

  const handleLoadSuggestionUpdate = useMemo(() => {
    return throttle(() => {
      invalidateLoadSuggestions();
      setShouldAnimateOnEnter(true);
    }, 1_000);
  }, [setShouldAnimateOnEnter, invalidateLoadSuggestions]);

  useAPISocketMessages((payload) => {
    if (payload.meta.type === 'load_suggestions_updated') {
      handleLoadSuggestionUpdate();
    }
  });

  const stringifiedEventPayload = useMemo(
    () =>
      JSON.stringify(
        loads?.map((load) => ({
          guid: load.guid,
          posting_guid: load.posting_guid,
          load_suggestion_guid: load.suggestion_guid,
        })),
      ),
    [loads],
  );

  useCounterUpdater();
  useDocumentTitle('Suggested Loads');

  useEffect(() => {
    if (stringifiedEventPayload) {
      trackEvent('Driver Opened Suggested Loads Tab', {
        load_suggestions: stringifiedEventPayload,
      });
    }
  }, [stringifiedEventPayload]);

  useEffect(() => {
    if (isError) {
      addSnackbar(
        <Stack>
          <Typography variant="h4">Failed to fetch suggested loads</Typography>
          <Typography>
            Something went wrong. We&apos;re sorry for the inconvenience. Try
            again later or contact our support at{' '}
            <WebViewLink href="mailto:support@superdispatch.com">
              support@superdispatch.com
            </WebViewLink>{' '}
            if the problem persists.
          </Typography>
        </Stack>,
        { variant: 'error', autoHideDuration: 10000 },
      );
    }
  }, [isError, addSnackbar]);

  const { params } = usePostingsPageContext();
  const queryClient = useQueryClient();

  return (
    <WebviewLayout>
      <Box padding="small">
        <LoadSuggestionBenefitsBanner />

        {loads?.length === 0 && (
          <NotFoundPageWrapper>
            <LoadSuggestionsNotFound />
          </NotFoundPageWrapper>
        )}

        {isLoading && (
          <>
            <LoadSuggestionsLoadingSkeleton />
            <LoadSuggestionsLoadingSkeleton />
          </>
        )}

        {!!params.current_load_guid && <PostingDetailsPage />}

        <TransitionGroup>
          {loads?.map((load, idx) => (
            <Collapse
              key={load.guid}
              timeout={450}
              unmountOnExit={true}
              enter={shouldAnimateOnEnter}
            >
              <LoadSuggestionSwipeToDismiss
                onDismiss={() => {
                  dismiss(load.suggestion_id);
                }}
                isDismissing={isDismissing}
                shouldRunOnboarding={idx === 0} // should run onboarding only for the first item
              >
                <VisibilityObserver
                  onChange={(visibility) => {
                    if (
                      visibility === 'visible' &&
                      !load.is_seen &&
                      !viewedUnseenLoadSuggestionGuids.has(load.suggestion_id)
                    ) {
                      markAsSeen(new Set([load.suggestion_id]));
                      viewedUnseenLoadSuggestionGuids.add(load.suggestion_id);
                    }
                  }}
                  render={({ ref }) => (
                    <LoadSuggestionsListItem
                      key={load.guid}
                      load={load}
                      ref={ref}
                      shouldRunOnboarding={shouldRunOnboarding}
                      onClick={() => {
                        // We only want to track it here, not in the
                        // LoadSuggestionsListItem, because it's also rendered
                        // by the drawer that has its own event on click.
                        trackDriverOpenedSuggestion(load, 'Suggested Tab Bar');

                        if (!load.is_seen) {
                          setLoadSuggestionsQueryData(queryClient, (lds) =>
                            lds.map((nextLoad) => {
                              if (
                                nextLoad.suggestion_id === load.suggestion_id
                              ) {
                                return { ...nextLoad, is_seen: true };
                              }
                              return nextLoad;
                            }),
                          );
                        }
                      }}
                    />
                  )}
                />
              </LoadSuggestionSwipeToDismiss>
            </Collapse>
          ))}
        </TransitionGroup>
      </Box>
    </WebviewLayout>
  );
}
